import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'components/Modal'
import { ModalActionFooter } from 'components/Modal/ModalFooter'
import Input from 'components/Form/Input'
import InputTitle from 'components/Form/InputTitle'
import RadioButton from 'components/Form/RadioButton'
import FormRow from 'components/Form/FormRow'
import FormColumn from 'components/Form/FormColumn'
import { InputMask } from 'utils/inputMask'
import { AppContext } from 'contexts/AppContext'
import { Disposition, DispositionCorrection } from 'types/disposition'
import service from '../service'

/**
 * The backend reads the direction from the sign of `percentChange`, but a masked decimal input
 * cannot take a minus sign (`InputMask.FLOAT` sets `allowMinus: false`) and a typed one would be
 * easy to miss anyway, so the direction is picked explicitly and the amount stays unsigned.
 */
enum CorrectionDirection {
  DECREASE = 'decrease',
  INCREASE = 'increase',
}

/** Every expanded disposition row renders its own modal, so the radio group has to stay unique. */
const directionInputId = (
  dispositionId: Disposition['id'],
  value: CorrectionDirection | string
): string => `correctionDirection-${dispositionId}-${value}`

interface CorrectionModalProps {
  disposition: Disposition
  correction?: DispositionCorrection | null
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  onSaved: () => void
}

const CorrectionModal: React.FC<CorrectionModalProps> = ({
  disposition,
  correction,
  isOpen,
  setIsOpen,
  onSaved,
}) => {
  const { t } = useTranslation()
  const { addNotification } = useContext(AppContext)
  const [direction, setDirection] = useState<CorrectionDirection>(CorrectionDirection.DECREASE)
  const [percentChange, setPercentChange] = useState<string>('')
  const [correctionReason, setCorrectionReason] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setDirection(
      correction && correction.percentChange > 0
        ? CorrectionDirection.INCREASE
        : CorrectionDirection.DECREASE
    )
    setPercentChange(correction ? Math.abs(correction.percentChange).toString() : '')
    setCorrectionReason(correction?.correctionReason ?? '')
  }, [isOpen, correction])

  const parsedAmount = parseFloat(percentChange.replace(',', '.'))
  const isPercentChangeValid = !isNaN(parsedAmount) && parsedAmount > 0 && parsedAmount <= 100
  const isValid = isPercentChangeValid && correctionReason.trim().length > 0

  const signedPercentChange =
    direction === CorrectionDirection.INCREASE ? parsedAmount : -parsedAmount

  const basePrice = service.basePrice(disposition, correction)
  const isCorrected = basePrice !== disposition.price
  const correctedPrice = isPercentChangeValid
    ? service.correctedPrice(basePrice, signedPercentChange)
    : null

  const handleSave = (): Promise<void> =>
    service
      .upsertCorrection(
        {
          dispositionId: disposition.id,
          correctionId: correction?.id ?? null,
          percentChange: signedPercentChange,
          correctionReason: correctionReason.trim(),
        },
        addNotification,
        t
      )
      .then(success => {
        if (success) {
          onSaved()
        }
      })

  return (
    <Modal
      isOpen={isOpen}
      isLoading={isLoading}
      onClose={() => setIsOpen(false)}
      title={t(
        correction
          ? 'agencies.dispositions.corrections.editTitle'
          : 'agencies.dispositions.corrections.addTitle'
      )}
      footer={
        <ModalActionFooter
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          setIsOpen={setIsOpen}
          onAction={handleSave}
          actionText={t('common.save')}
          actionButtonDisabled={!isValid}
        />
      }
    >
      <div className='CorrectionModal'>
        <FormRow>
          <FormColumn>
            <InputTitle title={t('agencies.dispositions.corrections.direction')} />
            <div
              className='CorrectionModal__direction'
              data-cy='correction-direction'
            >
              {[CorrectionDirection.DECREASE, CorrectionDirection.INCREASE].map(item => (
                <RadioButton
                  key={item}
                  id={directionInputId(disposition.id, item)}
                  groupId={directionInputId(disposition.id, 'group')}
                  value={directionInputId(disposition.id, direction)}
                  onChange={() => setDirection(item)}
                >
                  {t(`agencies.dispositions.corrections.${item}`)}
                </RadioButton>
              ))}
            </div>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <Input
              dataCy='correction-percent-change'
              id='percentChange'
              title={t('agencies.dispositions.corrections.percentAmount')}
              placeholder={t('agencies.dispositions.corrections.percentAmount')}
              value={percentChange}
              maskType={InputMask.FLOAT}
              suffix='%'
              onChange={(value: string) => setPercentChange(value)}
              errors={
                percentChange && !isPercentChangeValid
                  ? [t('agencies.dispositions.corrections.percentChangeError')]
                  : undefined
              }
            />
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <div className='CorrectionModal__reason'>
              <span className='CorrectionModal__reason-title'>
                {t('agencies.dispositions.corrections.reason')}
              </span>
              <textarea
                data-cy='correction-reason'
                value={correctionReason}
                placeholder={t('agencies.dispositions.corrections.reasonPlaceholder')}
                onChange={event => setCorrectionReason(event.target.value)}
              />
            </div>
          </FormColumn>
        </FormRow>

        <div className='CorrectionModal__summary'>
          {isCorrected && (
            <div className='CorrectionModal__summary-item'>
              <span>{t('agencies.dispositions.corrections.dispositionPrice')}</span>
              <strong>{service.formatPrice(disposition.price)}</strong>
            </div>
          )}
          <div className='CorrectionModal__summary-item'>
            <span>{t('agencies.dispositions.corrections.originalPrice')}</span>
            <strong data-cy='correction-base-price'>{service.formatPrice(basePrice)}</strong>
          </div>
          <div className='CorrectionModal__summary-item'>
            <span>{t('agencies.dispositions.corrections.correctedPrice')}</span>
            <strong data-cy='correction-corrected-price'>
              {correctedPrice === null ? '—' : service.formatPrice(correctedPrice)}
            </strong>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default CorrectionModal
